import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { StatCard } from '../components/ui/stat-card';
import { Clock, LogIn, LogOut, User } from 'lucide-react';
import { zktecoAPI, api } from '../services/api';
import { buildEmployeeLookups, enrichRecordsWithEmployeeData } from '../utils/employeeLookup';
import MonitoringHeader from '../components/zkteco/MonitoringHeader';
import MonitoringStatusBar from '../components/zkteco/MonitoringStatusBar';
import TerminalStatusCard from '../components/zkteco/TerminalStatusCard';
import PunchLogsCard from '../components/zkteco/PunchLogsCard';
import ActivitySummaryCard from '../components/zkteco/ActivitySummaryCard';

const ZktecoMonitoring = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [wsPunches, setWsPunches] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [deviceFilter, setDeviceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('today');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(10); // seconds
  const [currentPage, setCurrentPage] = useState(1);
  const [refreshProgress, setRefreshProgress] = useState(0);
  
  // États pour WebSocket et temps réel
  const [realtimeEnabled, setRealtimeEnabled] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [realtimeCount, setRealtimeCount] = useState(0);
  const [lastRealtimeActivity, setLastRealtimeActivity] = useState(null);
  
  const intervalRef = useRef(null);
  const wsRef = useRef(null);
  const progressTimerRef = useRef(null);
  
  const [logsQ, terminalsQ, employeesQ] = useQueries({
    queries: [
      {
        queryKey: ['punch-logs', currentPage, deviceFilter, statusFilter, dateFilter],
        queryFn: async () => {
          const params = { page: currentPage, limit: 50 };
          const today = new Date();
          if (dateFilter === 'today') {
            params.start_date = new Date(new Date(today).setHours(0, 0, 0, 0)).toISOString();
            params.end_date = new Date(new Date(today).setHours(23, 59, 59, 999)).toISOString();
          } else if (dateFilter === 'yesterday') {
            const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
            params.start_date = new Date(new Date(yesterday).setHours(0, 0, 0, 0)).toISOString();
            params.end_date = new Date(new Date(yesterday).setHours(23, 59, 59, 999)).toISOString();
          } else if (dateFilter === 'week') {
            params.start_date = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
            params.end_date = today.toISOString();
          }
          if (deviceFilter !== 'all') params.device_sn = deviceFilter;
          const response = await zktecoAPI.getPunchLogs(params);
          return response.data || {};
        },
        refetchInterval: autoRefresh ? refreshInterval * 1000 : false,
      },
      {
        queryKey: ['monitoring-terminals'],
        queryFn: () =>
          zktecoAPI.getTerminals({ limit: 1000 }).then((r) => r.data?.terminals || []),
      },
      {
        queryKey: ['monitoring-employees'],
        queryFn: () =>
          api.get('v1/employees/', { params: { limit: 1000 } }).then((r) => r.data?.employees || []),
      },
    ],
  });

  const employees = employeesQ.data ?? [];
  const employeeLookups = useMemo(() => buildEmployeeLookups(employees), [employees]);
  const terminals = terminalsQ.data ?? [];
  const rawPunches = useMemo(() => logsQ.data?.punches ?? [], [logsQ.data]);
  const totalPages = Math.ceil((logsQ.data?.total ?? rawPunches.length) / 50) || 1;
  const loading = logsQ.isPending;

  const punchLogs = useMemo(() => {
    const merged = [...wsPunches, ...rawPunches].slice(0, 1000);
    return enrichRecordsWithEmployeeData(merged, employeeLookups);
  }, [wsPunches, rawPunches, employeeLookups]);

  const stats = useMemo(() => ({
    totalToday: punchLogs.length,
    checkIns: punchLogs.filter((p) => p.status === 0 || p.status === 8 || p.status === 9).length,
    checkOuts: punchLogs.filter((p) => p.status === 1 || p.status === 15 || p.status === 16).length,
    uniqueUsers: new Set(punchLogs.map((p) => p.user_id)).size,
  }), [punchLogs]);

  const loadPunchLogs = () => queryClient.invalidateQueries({ queryKey: ['punch-logs'] });
  // Use Sonner's toast (mounted in App.js) for consistent notifications
  const showToast = ({ title, description, variant }) => {
    if (variant === 'destructive') {
      toast.error(title || t('common.error'), { description });
      return;
    }
    // Heuristic: success styling when explicitly successful
    if (title && (title.includes('✅') || /réussi|terminé/i.test(title))) {
      toast.success(title, { description });
      return;
    }
    toast(title || '', { description });
  };

  useEffect(() => {
    if (realtimeEnabled) {
      initializeWebSocket();
    }
    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, [realtimeEnabled]);

  // Progress indicator for auto refresh
  useEffect(() => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
    }
    if (!autoRefresh) {
      setRefreshProgress(0);
      return;
    }
    const startedAt = Date.now();
    progressTimerRef.current = setInterval(() => {
      const elapsed = (Date.now() - startedAt) / 1000;
      const value = ((elapsed % refreshInterval) / refreshInterval) * 100;
      setRefreshProgress(value);
    }, 500);
    return () => {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    };
  }, [autoRefresh, refreshInterval]);



  const filteredLogs = punchLogs.filter(log => {
    const matchesSearch = log.user_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (log.terminal_name && log.terminal_name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'checkin' && log.status === 0) ||
                         (statusFilter === 'checkout' && log.status === 1);
    
    return matchesSearch && matchesStatus;
  });

  const exportLogs = () => {
    // Simple CSV export
    const headers = [t('zktecoMonitoring.csvEmployeeId'), t('zktecoMonitoring.csvTimestamp'), t('zktecoMonitoring.csvDirection'), t('zktecoMonitoring.csvTerminal'), t('zktecoMonitoring.csvVerificationMode')];
    const csvContent = [
      headers.join(','),
      ...filteredLogs.map(log => [
        log.user_id,
        log.timestamp,
        log.attendance_direction,
        log.terminal_name || log.device_sn,
        log.verification_mode
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pointages_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const diagnoseTerminal = async (terminalId) => {
    try {
      showToast({
        title: t("zktecoMonitoring.diagLoadingTitle"),
        description: t("zktecoMonitoring.diagLoadingDesc"),
      });

      const response = await api.get(`v1/zkteco/terminals/${terminalId}/diagnose`);
      const data = response.data;

      if (data.success) {
        showToast({
          title: t("zktecoMonitoring.diagDoneTitle"),
          description: t("zktecoMonitoring.diagDoneDesc", { att: data.device_info.attendance_count, users: data.device_info.users_count }),
        });
      } else {
        showToast({
          title: t("zktecoMonitoring.diagErrorTitle"),
          description: data.message,
          variant: "destructive"
        });
      }
    } catch (error) {
      showToast({
        title: t("zktecoMonitoring.errorGeneric"),
        description: t("zktecoMonitoring.diagErrorDesc"),
        variant: "destructive"
      });
    }
  };

  const downloadAttendanceFromTerminal = async (terminalId) => {
    try {
      showToast({
        title: t("zktecoMonitoring.dlLoadingTitle"),
        description: t("zktecoMonitoring.dlLoadingDescOne"),
      });

      const response = await zktecoAPI.downloadAttendance(terminalId);

      if (response.data.success) {
        showToast({
          title: t("zktecoMonitoring.dlSuccessTitle"),
          description: response.data.message,
        });

        // Recharger les données après téléchargement
        await loadPunchLogs();
      } else {
        showToast({
          title: t("zktecoMonitoring.dlErrorTitle"),
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      showToast({
        title: t("zktecoMonitoring.dlErrorTitle"),
        description: error.response?.data?.detail || t("zktecoMonitoring.dlErrorDesc"),
        variant: "destructive",
      });
    }
  };

  const downloadAllTerminals = async () => {
    try {
      
      showToast({
        title: t("zktecoMonitoring.dlLoadingTitle"),
        description: t("zktecoMonitoring.dlLoadingDescAll"),
      });

      let successCount = 0;
      let errorCount = 0;

      for (const terminal of terminals) {
        try {
          const response = await zktecoAPI.downloadAttendance(terminal.id);
          
          if (response.data.success) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          errorCount++;
        }
      }


      showToast({
        title: t("zktecoMonitoring.dlDoneAllTitle"),
        description: t("zktecoMonitoring.dlDoneAllDesc", { count: successCount }) + (errorCount > 0 ? t("zktecoMonitoring.errorsSuffix", { count: errorCount }) : ''),
      });

      // Recharger les données après téléchargement
      await loadPunchLogs();

    } catch (error) {
      showToast({
        title: t("zktecoMonitoring.dlErrorTitle"),
        description: t("zktecoMonitoring.dlErrorMultiDesc"),
        variant: "destructive",
      });
    }
  };

  const clearAllTerminals = async () => {
    if (!window.confirm(t('zktecoMonitoring.clearConfirm'))) {
      return;
    }

    try {

      showToast({
        title: t("zktecoMonitoring.clearLoadingTitle"),
        description: t("zktecoMonitoring.clearLoadingDesc"),
      });

      let successCount = 0;
      let errorCount = 0;

      for (const terminal of terminals) {
        try {
          const response = await zktecoAPI.clearAttendance(terminal.id);
          
          if (response.data.success) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          errorCount++;
        }
      }


      showToast({
        title: t("zktecoMonitoring.clearDoneTitle"),
        description: t("zktecoMonitoring.clearDoneDesc", { count: successCount }) + (errorCount > 0 ? t("zktecoMonitoring.errorsSuffix", { count: errorCount }) : ''),
      });

      // Recharger les données après effacement
      await loadPunchLogs();

    } catch (error) {
      showToast({
        title: t("zktecoMonitoring.clearErrorTitle"),
        description: t("zktecoMonitoring.clearErrorDesc"),
        variant: "destructive",
      });
    }
  };

  // Fonctions WebSocket
  const initializeWebSocket = () => {
    try {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      // Use nginx proxy for WebSocket connection (no port needed in production)
      const backendHost = window.location.hostname;
      const wsUrl = `${wsProtocol}//${backendHost}/api/v1/zkteco/ws/monitoring`;
      
      
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        setWsConnected(true);
        showToast({
          title: t("zktecoMonitoring.wsConnectedTitle"),
          description: t("zktecoMonitoring.wsConnectedDesc"),
        });
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          handleWebSocketMessage(message);
        } catch (error) {
        }
      };
      
      wsRef.current.onclose = () => {
        setWsConnected(false);
        
        // Tentative de reconnexion après 5 secondes
        if (realtimeEnabled) {
          setTimeout(() => {
            initializeWebSocket();
          }, 5000);
        }
      };
      
      wsRef.current.onerror = (error) => {
        setWsConnected(false);
      };
      
    } catch (error) {
    }
  };

  const handleWebSocketMessage = (message) => {
    
    switch (message.type) {
      case 'realtime_attendance':
        handleRealtimeAttendance(message.records);
        break;
      case 'terminal_status':
        handleTerminalStatusUpdate(message.terminal);
        break;
      case 'connection_established':
        break;
      case 'heartbeat':
        break;
      default:
    }
  };

  const handleRealtimeAttendance = (records) => {
    
    setRealtimeCount(prev => prev + records.length);
    setLastRealtimeActivity(new Date());
    
    // Ajouter les nouveaux pointages au début de la liste (via WS state)
    setWsPunches((prev) => [...records, ...prev].slice(0, 1000));
    
    // Notification pour chaque pointage temps réel
    records.forEach(record => {
      const direction = record.status === 0 ? t('zktecoMonitoring.rtEntry') : t('zktecoMonitoring.rtExit');
      const icon = record.status === 0 ? '🟢' : '🔴';

      showToast({
        title: t('zktecoMonitoring.rtPunchTitle', { icon, direction }),
        description: t('zktecoMonitoring.rtPunchDesc', { user: record.user_id, terminal: record.terminal_name || t('zktecoMonitoring.rtTerminalFallback') }),
        duration: 3000,
      });
    });
  };

  const handleTerminalStatusUpdate = (terminalInfo) => {
    queryClient.setQueryData(['monitoring-terminals'], (prev) =>
      (prev || []).map((t) =>
        t.ip_address === terminalInfo.ip_address ? { ...t, ...terminalInfo } : t
      )
    );
  };

  const toggleRealtimeMode = () => {
    setRealtimeEnabled(!realtimeEnabled);
    
    if (!realtimeEnabled) {
      initializeWebSocket();
    } else {
      if (wsRef.current) {
        wsRef.current.close();
      }
      setWsConnected(false);
    }
  };





  return (
    <div className="space-y-6">
      <MonitoringHeader
        realtimeEnabled={realtimeEnabled}
        wsConnected={wsConnected}
        toggleRealtimeMode={toggleRealtimeMode}
        autoRefresh={autoRefresh}
        setAutoRefresh={setAutoRefresh}
        refreshInterval={refreshInterval}
        setRefreshInterval={setRefreshInterval}
        downloadAllTerminals={downloadAllTerminals}
        clearAllTerminals={clearAllTerminals}
        exportLogs={exportLogs}
        loadPunchLogs={loadPunchLogs}
      />

      <MonitoringStatusBar
        realtimeEnabled={realtimeEnabled}
        wsConnected={wsConnected}
        realtimeCount={realtimeCount}
        lastRealtimeActivity={lastRealtimeActivity}
        autoRefresh={autoRefresh}
        refreshInterval={refreshInterval}
        setRefreshInterval={setRefreshInterval}
        refreshProgress={refreshProgress}
      />

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard title={t("zktecoMonitoring.statToday")} value={stats.totalToday} icon={Clock} />
        <StatCard title={t("zktecoMonitoring.statCheckIns")} value={stats.checkIns} icon={LogIn} />
        <StatCard title={t("zktecoMonitoring.statCheckOuts")} value={stats.checkOuts} icon={LogOut} />
        <StatCard title={t("zktecoMonitoring.statUniqueUsers")} value={stats.uniqueUsers} icon={User} />
      </div>

      <TerminalStatusCard
        terminals={terminals}
        diagnoseTerminal={diagnoseTerminal}
        downloadAttendanceFromTerminal={downloadAttendanceFromTerminal}
        showToast={showToast}
      />

      <PunchLogsCard
        filteredLogs={filteredLogs}
        loading={loading}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        dateFilter={dateFilter}
        setDateFilter={setDateFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        deviceFilter={deviceFilter}
        setDeviceFilter={setDeviceFilter}
        terminals={terminals}
        wsConnected={wsConnected}
        autoRefresh={autoRefresh}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
      />

      <ActivitySummaryCard stats={stats} />
    </div>
  );
};

export default ZktecoMonitoring; 
